import { Controller } from "@hotwired/stimulus"

let actionMenuSequence = 0

// Details keep the native disclosure trigger while Popover moves the menu into
// the top layer, outside any scroll container that could clip it.
export default class extends Controller {
  static targets = ["summary", "panel"]

  connect() {
    this._onDocumentClick = this.#documentClick.bind(this)
    this._onDocumentKeydown = this.#documentKeydown.bind(this)
    this._onViewportChange = this.#viewportChange.bind(this)

    if (this.hasPanelTarget) {
      this.panelTarget.id ||= `estimate-action-menu-${++actionMenuSequence}`
      this.panelTarget.setAttribute("popover", "manual")
      this._popoverSupported = typeof this.panelTarget.showPopover === "function"
      if (this._popoverSupported) {
        Object.assign(this.panelTarget.style, {
          bottom: "auto",
          inset: "auto",
          margin: "0",
          position: "fixed",
          right: "auto"
        })
      }
    }
    if (this.hasSummaryTarget && this.hasPanelTarget) {
      this.summaryTarget.setAttribute("aria-controls", this.panelTarget.id)
    }

    document.addEventListener("click", this._onDocumentClick)
    document.addEventListener("keydown", this._onDocumentKeydown)
    document.addEventListener("scroll", this._onViewportChange, true)
    window.addEventListener("resize", this._onViewportChange, { passive: true })
    this.sync()
  }

  disconnect() {
    this.#hidePopover()
    document.removeEventListener("click", this._onDocumentClick)
    document.removeEventListener("keydown", this._onDocumentKeydown)
    document.removeEventListener("scroll", this._onViewportChange, true)
    window.removeEventListener("resize", this._onViewportChange)
  }

  sync() {
    if (this.hasSummaryTarget) this.summaryTarget.setAttribute("aria-expanded", this.element.open ? "true" : "false")
    if (!this.hasPanelTarget) return

    if (this.element.open) {
      if (this._popoverSupported && !this.#popoverOpen()) this.panelTarget.showPopover()
      requestAnimationFrame(() => this.#placePanel())
    } else {
      this.#hidePopover()
    }
  }

  #placePanel() {
    if (!this.hasPanelTarget || !this.hasSummaryTarget) return

    if (this._popoverSupported) {
      const triggerRect = this.summaryTarget.getBoundingClientRect()
      const panelRect = this.panelTarget.getBoundingClientRect()
      const gutter = 8
      const viewportWidth = document.documentElement.clientWidth
      const viewportHeight = document.documentElement.clientHeight
      const left = Math.min(
        Math.max(gutter, triggerRect.right - panelRect.width),
        viewportWidth - panelRect.width - gutter
      )
      const preferredTop = triggerRect.bottom + gutter
      const top = preferredTop + panelRect.height <= viewportHeight - gutter ?
        preferredTop : Math.max(gutter, triggerRect.top - panelRect.height - gutter)

      this.panelTarget.style.left = `${left}px`
      this.panelTarget.style.top = `${top}px`
      return
    }

    const clippingContainer = this.element.closest(".overflow-x-auto")
    this.panelTarget.classList.remove("bottom-full", "mb-2")
    this.panelTarget.classList.add("mt-2")
    if (!clippingContainer) return

    const panelRect = this.panelTarget.getBoundingClientRect()
    const clipRect = clippingContainer.getBoundingClientRect()
    if (panelRect.bottom > clipRect.bottom) {
      this.panelTarget.classList.remove("mt-2")
      this.panelTarget.classList.add("bottom-full", "mb-2")
    }
  }

  #documentClick(event) {
    if (this.element.open && !event.composedPath().includes(this.element)) {
      this.element.open = false
      this.sync()
    }
  }

  #documentKeydown(event) {
    if (event.key !== "Escape" || !this.element.open) return

    event.preventDefault()
    this.element.open = false
    this.sync()
    this.summaryTarget?.focus()
  }

  #viewportChange() {
    if (this.element.open) this.#placePanel()
  }

  #popoverOpen() {
    return this._popoverSupported && this.panelTarget.matches(":popover-open")
  }

  #hidePopover() {
    if (this.#popoverOpen()) this.panelTarget.hidePopover()
  }
}
